import { DownOutlined, SearchOutlined } from '@ant-design/icons';
import { Checkbox, Dropdown, Flex } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import Styles from './styles';
import pluralize from 'pluralize';
import { useDebouncedCallback } from 'use-debounce';
import { useOrganizationMembers } from '../../../../organizations/hooks/useOrganizationMembers';
import { EllipsisWithTooltip } from '../../../../shared/components/EllipsisWithTooltip';

const { CustomInput, OptionsContainer, SelectAllHeader, Contents, OptionLabel, DropdownOption } =
  Styles;

type Options = {
  id: string;
  label: string;
  selected: boolean;
};

type ParticipantFilter = {
  email: string;
  host: boolean;
  participant: boolean;
};

type Props = {
  value: string[];
  onChange: (value: ParticipantFilter[]) => void;
};

type ParticipantsAndHosts = {
  participants: Record<string, boolean>;
  hosts: Record<string, boolean>;
};

export const MeetingParticipantsSelector: React.FC<Props> = ({ value = [], onChange }) => {
  const { members } = useOrganizationMembers();

  const [currentOptions, setCurrentOptions] = useState<Options[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const [participantsAndHosts, setParticipantsAndHosts] = useState<ParticipantsAndHosts>({
    participants: {},
    hosts: {},
  });

  useEffect(() => {
    setCurrentOptions(
      members.map(member => ({
        id: member.user.id,
        label: `${member.user.firstName} ${member.user.lastName}`,
        selected: value.includes(member.user.id),
      })),
    );
  }, [members, value]);

  const searchDebounce = useDebouncedCallback(value => {
    setSearchValue(value);
  }, 200);

  const onChangeDebounced = useDebouncedCallback(value => {
    onChange(value);
  }, 1000);

  const filteredOptions = useMemo(() => {
    if (searchValue) {
      return currentOptions.filter(o => o.label.toLowerCase().includes(searchValue.toLowerCase()));
    }

    return currentOptions;
  }, [currentOptions, searchValue]);

  const optionsCount = currentOptions.length;
  const hasOptions = optionsCount > 0;

  const handleDataChange = (data: {
    hosts: Record<string, boolean>;
    participants: Record<string, boolean>;
  }) => {
    onChangeDebounced(
      members
        .filter(member => !!data.hosts[member.user.id] || !!data.participants[member.user.id])
        .map(member => ({
          email: member.user.email,
          host: !!data.hosts[member.user.id],
          participant: !!data.participants[member.user.id],
        })),
    );
  };

  const handleHostChange = (id: string, checked: boolean) => {
    const newData = {
      ...participantsAndHosts,
      hosts: {
        ...participantsAndHosts.hosts,
        [id]: checked,
      },
    };
    setParticipantsAndHosts(newData);
    handleDataChange(newData);
  };

  const handleParticipantChange = (id: string, checked: boolean) => {
    const newData = {
      ...participantsAndHosts,
      participants: {
        ...participantsAndHosts.participants,
        [id]: checked,
      },
    };
    setParticipantsAndHosts(newData);
    handleDataChange(newData);
  };

  const inputPlaceholder = useMemo(() => {
    if (!hasOptions) {
      return `No ${pluralize('Participant')} available`;
    }

    if (
      Object.values(participantsAndHosts.hosts).length > 0 ||
      Object.values(participantsAndHosts.participants).length > 0
    ) {
      const getParticipantsEmails = (obj: Record<string, boolean>) => {
        return Object.entries(obj)
          .filter(([, value]) => value)
          .map(([key]) => key);
      };

      const participantsSet = new Set([
        ...getParticipantsEmails(participantsAndHosts.hosts),
        ...getParticipantsEmails(participantsAndHosts.participants),
      ]);

      return `${participantsSet.size} ${pluralize('Participant', participantsSet.size)} selected`;
    }

    return `Select ${pluralize('Participant')}`;
  }, [hasOptions, participantsAndHosts]);

  return (
    <Dropdown
      dropdownRender={() => (
        <Contents style={{ minWidth: '300px' }}>
          <SelectAllHeader>
            <OptionLabel>
              <Flex justify="space-between">
                <span></span>
                <Flex style={{ width: '110px' }} gap={8}>
                  <span>Host</span>
                  <span>Participant</span>
                </Flex>
              </Flex>
            </OptionLabel>
          </SelectAllHeader>

          <OptionsContainer>
            {filteredOptions.map(option => {
              return (
                <DropdownOption key={option.id}>
                  <OptionLabel style={{ overflow: 'hidden', width: '100%' }}>
                    <Flex justify="space-between">
                      <EllipsisWithTooltip text={option.label} />

                      <Flex style={{ width: '155px' }} gap={24}>
                        <Checkbox
                          checked={participantsAndHosts.hosts[option.id]}
                          onChange={e => handleHostChange(option.id, e.target.checked)}
                        />

                        <Checkbox
                          checked={participantsAndHosts.participants[option.id]}
                          onChange={e => handleParticipantChange(option.id, e.target.checked)}
                        />
                      </Flex>
                    </Flex>
                  </OptionLabel>
                </DropdownOption>
              );
            })}
          </OptionsContainer>
        </Contents>
      )}
      trigger={['click']}
      onOpenChange={open => setDropdownOpen(open)}
    >
      <CustomInput
        type="search"
        className={value.length ? 'has-value' : ''}
        placeholder={inputPlaceholder}
        disabled={!hasOptions}
        style={{ maxWidth: '200px' }}
        onKeyUp={e => searchDebounce(e.currentTarget.value)}
        suffix={
          !dropdownOpen ? (
            <DownOutlined
              style={{
                transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
                width: '12px',
                height: '12px',
                color: 'rgba(0, 0, 0, 0.25)',
              }}
            />
          ) : (
            <SearchOutlined
              style={{
                width: '12px',
                height: '12px',
                color: 'rgba(0, 0, 0, 0.25)',
              }}
            />
          )
        }
      />
    </Dropdown>
  );
};
